/**
 * Mention Parser - Validation Functions
 *
 * Functions for validating structured mention tokens.
 */

import { STRUCTURED_MENTION_USER_ID_REGEX } from "./regex.js";

/**
 * Validate and normalize structured mention tokens.
 *
 * - Keeps valid tokens in canonical format: @[Display Name](userId)
 * - Converts invalid/malformed tokens into plain text @Display Name fallback
 *   so malformed structured tokens never persist to storage.
 *
 * @param {string} text
 * @returns {{ text: string, hadInvalidTokens: boolean, invalidTokenCount: number }}
 */
export function validateStructuredMentions(text) {
  if (!text || typeof text !== "string") {
    return { text: text || "", hadInvalidTokens: false, invalidTokenCount: 0 };
  }

  const normalized = String(text);
  const tokenRegex = /@\[([^\]]*)\]\(([^)]*)\)/g;
  let match;
  let cursor = 0;
  let result = "";
  let invalidTokenCount = 0;

  while ((match = tokenRegex.exec(normalized)) !== null) {
    const [rawToken, rawDisplayName, rawUserId] = match;
    const matchStart = match.index;
    const matchEnd = matchStart + rawToken.length;

    result += normalized.slice(cursor, matchStart);

    const displayName = String(rawDisplayName || "").trim();
    const userId = String(rawUserId || "").trim();
    const isValid =
      displayName.length > 0 &&
      userId.length > 0 &&
      STRUCTURED_MENTION_USER_ID_REGEX.test(userId);

    if (isValid) {
      result += `@[${displayName}](${userId})`;
    } else {
      invalidTokenCount += 1;
      result += displayName ? `@${displayName}` : "@mention";
    }

    cursor = matchEnd;
  }

  result += normalized.slice(cursor);

  const malformedStartCount = (normalized.match(/@\[/g) || []).length;
  const validLikeCount = (normalized.match(tokenRegex) || []).length;
  if (malformedStartCount > validLikeCount) {
    invalidTokenCount += malformedStartCount - validLikeCount;
    result = result.replace(/@\[/g, "@");
  }

  return {
    text: result,
    hadInvalidTokens: invalidTokenCount > 0,
    invalidTokenCount,
  };
}
